import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Button,
  VStack,
  Text,
  HStack,
  Box,
  useToast,
  Spinner,
  Collapse,
  Alert,
  AlertIcon,
} from "@chakra-ui/react";
import { ChevronDownIcon, ChevronUpIcon } from "@chakra-ui/icons";
import { useState, useMemo } from "react";
import actions from "src/actions";
import type { EnrichedCustomConversion } from "./types";
import ConversionCard from "./ConversionCard";

const DEFAULT_CUSTOM_CONVERSIONS_LIMIT = 100;

export type ManageCustomConversionsModalProps = {
  isOpen: boolean;
  onClose: () => void;
  conversions: EnrichedCustomConversion[];
  ad_account_id: string;
  /** Meta limit for custom conversions (from API meta config); fallback used if not provided */
  customConversionsLimit?: number;
  /** Called after successful deletion; can return a Promise. Refetch conversions here. */
  onDeleted?: () => void | Promise<void>;
};

export default function ManageCustomConversionsModal({
  isOpen,
  onClose,
  conversions,
  ad_account_id,
  customConversionsLimit = DEFAULT_CUSTOM_CONVERSIONS_LIMIT,
  onDeleted,
}: ManageCustomConversionsModalProps) {
  const limit = customConversionsLimit ?? DEFAULT_CUSTOM_CONVERSIONS_LIMIT;
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [deleting, setDeleting] = useState(false);
  const [pastExpanded, setPastExpanded] = useState(false);
  const [futureExpanded, setFutureExpanded] = useState(false);
  const toast = useToast();

  const { past, future, unknown } = useMemo(() => {
    const past: EnrichedCustomConversion[] = [];
    const future: EnrichedCustomConversion[] = [];
    const unknown: EnrichedCustomConversion[] = [];
    for (const c of conversions) {
      if (c.deletable) past.push(c);
      else if (c.event_summary) future.push(c);
      else unknown.push(c);
    }
    return { past, future, unknown };
  }, [conversions]);

  const pastIds = useMemo(() => past.map((c) => c.id), [past]);
  const futureIds = useMemo(() => future.map((c) => c.id), [future]);

  const hasFutureSelected = useMemo(
    () => futureIds.some((id) => selectedIds.has(id)),
    [futureIds, selectedIds]
  );

  const allPastSelected =
    pastIds.length > 0 && pastIds.every((id) => selectedIds.has(id));
  const allFutureSelected =
    futureIds.length > 0 && futureIds.every((id) => selectedIds.has(id));

  const handleSelectAllPastEvents = () => {
    if (allPastSelected) {
      const pastSet = new Set(pastIds);
      setSelectedIds(
        (prev) => new Set(Array.from(prev).filter((id) => !pastSet.has(id)))
      );
    } else {
      setSelectedIds((prev) => new Set([...Array.from(prev), ...pastIds]));
      setPastExpanded(true);
    }
  };

  const handleSelectAllFutureEvents = () => {
    if (allFutureSelected) {
      const futureSet = new Set(futureIds);
      setSelectedIds(
        (prev) => new Set(Array.from(prev).filter((id) => !futureSet.has(id)))
      );
    } else {
      setSelectedIds((prev) => new Set([...Array.from(prev), ...futureIds]));
      setFutureExpanded(true);
    }
  };

  const handleToggle = (id: string, selectable: boolean) => {
    if (!selectable) return;
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleDeleteSelected = async () => {
    if (selectedIds.size === 0) return;
    setDeleting(true);
    try {
      const res = await actions.ADMIN.deleteCustomConversions({
        ad_account_id,
        custom_conversion_ids: Array.from(selectedIds),
      });
      const deleted = (res?.deleted as string[] | undefined) ?? [];
      const errors = Array.isArray(res?.errors)
        ? (res.errors as Array<{ id?: string; message?: string }>)
        : [];

      if (errors.length === 0) {
        toast({
          title: "Custom conversions deleted",
          description: `${deleted.length} conversion(s) removed.`,
          status: "success",
          isClosable: true,
        });
        setSelectedIds(new Set());
        onClose();
        await onDeleted?.();
        return;
      }

      const failedIds = new Set(
        errors
          .map((e) => (typeof e?.id === "string" ? e.id : ""))
          .filter(Boolean)
      );
      setSelectedIds(failedIds);

      if (deleted.length === 0) {
        toast({
          title: "Delete failed",
          description:
            errors[0]?.message ||
            "None of the selected custom conversions could be removed.",
          status: "error",
          isClosable: true,
        });
        return;
      }

      toast({
        title: "Partial deletion completed",
        description: `${deleted.length} deleted, ${errors.length} failed.`,
        status: "warning",
        isClosable: true,
      });
      await onDeleted?.();
    } catch (err) {
      toast({
        title: "Delete failed",
        description: (err as Error).message,
        status: "error",
        isClosable: true,
      });
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Manage custom conversions</ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={6}>
          <Text fontSize="sm" color="gray.600" mb={4}>
            Meta allows up to {limit} custom conversions per ad account. Delete
            conversions from past events to free space for new ones.
          </Text>

          <VStack align="stretch" spacing={4} maxH="50vh" overflowY="auto">
            {conversions.length === 0 ? (
              <Text fontSize="sm" color="gray.500">
                No custom conversions found.
              </Text>
            ) : (
              <>
                {past.length > 0 ? (
                  <Box>
                    <HStack
                      mb={2}
                      justify="space-between"
                      align="center"
                      wrap="wrap"
                      gap={2}
                    >
                      <Box
                        as="button"
                        type="button"
                        flex={1}
                        minW={0}
                        textAlign="left"
                        fontSize="xs"
                        fontWeight="semibold"
                        color="gray.600"
                        textTransform="uppercase"
                        letterSpacing="wider"
                        onClick={() => setPastExpanded((e) => !e)}
                        display="flex"
                        alignItems="center"
                        gap={2}
                        _hover={{ color: "gray.800" }}
                      >
                        Past events ({past.length})
                        {pastExpanded ? (
                          <ChevronUpIcon boxSize={4} />
                        ) : (
                          <ChevronDownIcon boxSize={4} />
                        )}
                      </Box>
                      <Button
                        size="xs"
                        variant="outline"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleSelectAllPastEvents();
                        }}
                      >
                        {allPastSelected ? "Unselect all" : "Select all"}
                      </Button>
                    </HStack>
                    <Collapse in={pastExpanded}>
                      <VStack align="stretch" spacing={2} pt={0}>
                        {past.map((c) => (
                          <ConversionCard
                            key={c.id}
                            c={c}
                            selectedIds={selectedIds}
                            onToggle={handleToggle}
                            selectable
                            badge="Past event"
                            badgeColor="green.600"
                          />
                        ))}
                      </VStack>
                    </Collapse>
                  </Box>
                ) : null}
                {future.length > 0 ? (
                  <Box>
                    <HStack
                      mb={2}
                      justify="space-between"
                      align="center"
                      wrap="wrap"
                      gap={2}
                    >
                      <Box
                        as="button"
                        type="button"
                        flex={1}
                        minW={0}
                        textAlign="left"
                        fontSize="xs"
                        fontWeight="semibold"
                        color="gray.600"
                        textTransform="uppercase"
                        letterSpacing="wider"
                        onClick={() => setFutureExpanded((e) => !e)}
                        display="flex"
                        alignItems="center"
                        gap={2}
                        _hover={{ color: "gray.800" }}
                      >
                        Future events ({future.length})
                        {futureExpanded ? (
                          <ChevronUpIcon boxSize={4} />
                        ) : (
                          <ChevronDownIcon boxSize={4} />
                        )}
                      </Box>
                      <Button
                        size="xs"
                        variant="outline"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleSelectAllFutureEvents();
                        }}
                      >
                        {allFutureSelected ? "Unselect all" : "Select all"}
                      </Button>
                    </HStack>
                    <Collapse in={futureExpanded}>
                      <VStack align="stretch" spacing={2} pt={0}>
                        {future.map((c) => (
                          <ConversionCard
                            key={c.id}
                            c={c}
                            selectedIds={selectedIds}
                            onToggle={handleToggle}
                            selectable
                            badge="Active"
                            badgeColor="gray.400"
                            dateLabel="Ends"
                          />
                        ))}
                      </VStack>
                    </Collapse>
                  </Box>
                ) : null}
                {unknown.length > 0 ? (
                  <Box>
                    <Text
                      fontSize="xs"
                      fontWeight="semibold"
                      color="gray.600"
                      textTransform="uppercase"
                      letterSpacing="wider"
                      mb={1}
                    >
                      Unknown
                    </Text>
                    <Text fontSize="xs" color="gray.500" mb={2}>
                      These are the custom conversions we couldn't find a
                      matching event for.
                    </Text>
                    <VStack align="stretch" spacing={2}>
                      {unknown.map((c) => (
                        <ConversionCard
                          key={c.id}
                          c={c}
                          selectedIds={selectedIds}
                          onToggle={handleToggle}
                          selectable
                          badge="No match"
                          badgeColor="orange.500"
                        />
                      ))}
                    </VStack>
                  </Box>
                ) : null}
              </>
            )}
          </VStack>

          {hasFutureSelected ? (
            <Alert status="warning" mt={4} borderRadius="md" fontSize="sm">
              <AlertIcon />
              Deleting custom conversions for future events means we will stop
              being able to track purchases on the platform.
            </Alert>
          ) : null}
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            colorScheme="red"
            isDisabled={selectedIds.size === 0 || deleting}
            onClick={handleDeleteSelected}
            leftIcon={deleting ? <Spinner size="sm" /> : undefined}
          >
            {deleting ? (
              "Deleting…"
            ) : (
              <>
                Confirm deletion
                {selectedIds.size > 0 && (
                  <Text as="span" fontWeight="bold" ml={1}>
                    ({selectedIds.size})
                  </Text>
                )}
              </>
            )}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
